<script setup>
import {Icon} from "@iconify/vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {onMounted, onUnmounted, provide, ref, computed} from "vue";
import {useHotOrdersStore} from "@/stores/hotOrders.js";
import {showMessage} from "@/utils/showMessage.js";
import LogModal from "@/views/updateDog/LogModal.vue";
import FiltersModal from "@/views/updateDog/FiltersModal.vue";
import UserProfile from "@/components/UserProfile/index.vue";
import {useUserStore} from "@/stores/user.js";
import {useFormattedDate} from '@/composables/useFormattedDate';
import {useConfirm} from "primevue/useconfirm";

//stores
const hotOrdersStore = useHotOrdersStore();
const userStore = useUserStore();
const confirm = useConfirm();

//composables
const {getFormattedDate} = useFormattedDate();

//component variables
const updateDogs = ref([])
const initialLoading = ref(true)
let stopLoop = false
const isShowUserProfileModal = ref(false);
const isShowAllRollsModal = ref(false);
const isShowFiltersModal = ref(false);
const selectedTakerID = ref(null)
const isShowPublishModal = ref(false);
const selectedItem = ref({})
const isShowItemLogModal = ref(false)
const currentUpdatedogs = ref(false)
const keyword = ref('')
const selectedGames = ref([])

const steps = ['New', 'Pending', 'Completed']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Pending: 'bg-[#FF9800]',
  Completed: 'bg-[#4CAF50]',
  Off: 'bg-[#F44336]'
}
const getStepIndex = (state) => steps.indexOf(state)

const filteredUpdateDogs = computed(() => {
  let list = updateDogs.value

  // فیلتر بر اساس current/upcoming
  if (currentUpdatedogs.value !== null) {
    const now = Date.now()

    list = list.filter(item => {
      // If it doesn't have fireTime -> it will only be displayed in All
      if (typeof item.fireTime !== 'number') return false

      const isPast = item.fireTime < now

      return currentUpdatedogs.value === false
          ? isPast
          : !isPast
    })
  }

  // فیلتر بر اساس keyword
  if (keyword.value.trim()) {
    const key = keyword.value.toLowerCase()

    list = list.filter(item => {
      return (
          item.orderTitle?.toLowerCase().includes(key) ||
          item.customer?.toLowerCase().includes(key) ||
          item.customerDiscordUsername?.toLowerCase().includes(key) ||
          item.takerName?.toLowerCase().includes(key) ||
          item.takerDiscordUsername?.toLowerCase().includes(key) ||
          item.orderUUID?.toLowerCase().includes(key) ||
          item.state?.toLowerCase().includes(key) ||
          String(item.orderID).includes(key)
      )
    })
  }

  if (selectedGames.value.length) {
    list = list.filter(item => selectedGames.value.includes(item.orderGame))
  }

  return list
})
const intervalOptions = {
  0: 'One-time only',
  3600000: 'Every 1 hour',
  7200000: 'Every 2 hours',
  14400000: 'Every 4 hours',
  21600000: 'Every 6 hours',
  28800000: 'Every 8 hours',
  43200000: 'Every 12 hours',
  86400000: 'Every 24 hours',
}

const getUpdateDogs = async () => {
  try {
    const res = await hotOrdersStore.action_getAllUpdateDogs();
    if (res.result === true) {
      updateDogs.value = res.data.sort((a, b) => {
        const aHas = a.lastPublish !== undefined
        const bHas = b.lastPublish !== undefined

        if (!aHas && !bHas) return 0
        if (!aHas) return 1
        if (!bHas) return -1

        return a.lastPublish - b.lastPublish
      })
    }/* else {
      showMessage(res, "error")
    }*/
  } catch (err) {
    showMessage(err, "error")
  } finally {
    initialLoading.value = false
  }
}

async function startRequestsLoop() {
  stopLoop = false
  while (!stopLoop) {
    await getUpdateDogs()
    await new Promise(resolve => setTimeout(resolve, 5000))
  }
}

function showUserProfileModal(takerID) {
  selectedTakerID.value = takerID
  isShowUserProfileModal.value = true
}

const handleState = (item, level) => {
  if (item.state === "Completed" || item.state === "Off") return;
  if ((item.state === 'New' && level === 1) || (item.state === 'Pending' && level === 2)) {
    updateState(item)
  }
}

const updateState = async (item) => {
  const foundIndex = updateDogs.value.findIndex(u => u.orderID === item.orderID)
  let newState;
  if (item.state === "New") {
    newState = "Pending"
  }
  if (item.state === "Pending") {
    newState = "Completed"
  }
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `You are changing the state to ${newState}`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Change',
      size: 'small'
    },
    accept: async () => {
      try {
        const request = {
          "orderID": item.orderID,
          "state": newState
        }

        const res = await hotOrdersStore.action_updateUpdateDog(request)

        if (res.result === true) {
          updateDogs.value[foundIndex].state = newState
          showMessage('State Changed', 'success');
        } else {
          showMessage(res, 'error');
        }
      } catch (err) {
        showMessage(err, 'error');
      } finally {
      }
    },
    reject: () => {
      return
    }
  });
}

function getTimePassed(lastPublish) {
  const now = Date.now()
  const diff = now - lastPublish // milliseconds
  const minutes = Math.floor(diff / 60000)

  if (minutes < 0) {
    const positiveMinutes = minutes * -1

    if (positiveMinutes < 1) return 'just now'
    if (positiveMinutes === 1) return `in ${positiveMinutes} min`
    if (positiveMinutes < 60) return `in ${positiveMinutes} mins`

    const hours = Math.floor(positiveMinutes / 60)
    return `in ${hours} hour${hours > 1 ? 's' : ''}`
  }

  if (minutes < 1) return 'just now'
  if (minutes === 1) return `${minutes} min ago`
  if (minutes < 60) return `${minutes} mins ago`

  const hours = Math.floor(minutes / 60)
  return `${hours} hour${hours > 1 ? 's' : ''} ago`
}

const showItemLog = (item) => {
  selectedItem.value = item
  isShowItemLogModal.value = true
}

const changeCurrentUpdatedogs = (arc) => {
  currentUpdatedogs.value = arc
}

const showFiltersModal = () => {
  isShowFiltersModal.value = true
}

onMounted(() => {
  startRequestsLoop()
})

onUnmounted(() => {
  stopLoop = true
})

provide('isShowUserProfileModal', isShowUserProfileModal)
provide('isShowAllRollsModal', isShowAllRollsModal)
provide('isShowFiltersModal', isShowFiltersModal)
provide('updateDogSelectedGames', selectedGames)
provide('isShowItemLogModal', isShowItemLogModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full overflow-hidden">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <div class="flex flex-row justify-between gap-2">
          <div
              class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
            <span>
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
              </svg>
            </span>
            <input v-model="keyword" type="text"
                   class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
            <button v-if="keyword"
                    @click="keyword = ''" class="hover:text-red-400">
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
              </svg>
            </button>
          </div>
          <button @click="showFiltersModal"
                  class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
                  :class="{'border !border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': selectedGames.length}">
            Filters {{ selectedGames.length ? '(' + selectedGames.length + ')' : '' }}
          </button>
          <div
              class="w-max border border-slate-200 dark:border-slate-700 dark:bg-slate-900 flex items-center rounded-md text-xs h-[32px]">
          <span
              class="flex-1 font-normal px-3 py-1 rounded-r ltr:rounded-l ltr:rounded-r-none transition-all duration-150 cursor-pointer h-full items-center flex"
              :class="
                    currentUpdatedogs === false
                      ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                      : 'dark:text-slate-300'
                  "
              @click="changeCurrentUpdatedogs(false)"
          >Current</span>
            <span
                class="flex-1 font-normal px-3 py-1 transition-all duration-150 cursor-pointer h-full items-center flex"
                :class="
          currentUpdatedogs === true
            ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
            : 'dark:text-slate-300'
        "
                @click="changeCurrentUpdatedogs(true)"
            >Upcoming</span>
            <span
                class="flex-1 font-normal px-3 py-1 rounded-l ltr:rounded-r ltr:rounded-l-none transition-all duration-150 cursor-pointer h-full items-center flex"
                :class="
                  currentUpdatedogs === null
                    ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                    : 'dark:text-slate-300'
                "
                @click="changeCurrentUpdatedogs(null)"
            >All</span>
          </div>
        </div>
      </div>
      <div v-if="initialLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <div v-else class="flex-1 overflow-y-auto custom-scrollbar pe-2">
        <div v-if="filteredUpdateDogs.length > 0"
             class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 text-slate-300 text-sm gap-8 md:gap-6 lg:gap-8">
          <div v-for="(item, index) in filteredUpdateDogs" :key="item.orderID"
               class="p-2 rounded-xl relative flex flex-col gap-2 border border-slate-600 bg-slate-800">
            <div class="flex justify-between items-center">
              <div class="flex flex-row gap-2 items-center">
                <p class="text-blue-400">{{ item.orderUUID ? item.orderUUID : item.orderID }}</p>
                <router-link
                    :to="{name: 'boost-order-details', params: {id: item.orderUUID ? (item.orderUUID).split('-')[0] : item.orderID}}"
                    target="_blank" class="cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-info-icon lucide-info">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                </router-link>
                <button @click="showItemLog(item)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-clipboard-clock-icon lucide-clipboard-clock">
                    <path d="M16 14v2.2l1.6 1"/>
                    <path d="M16 4h2a2 2 0 0 1 2 2v.832"/>
                    <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2"/>
                    <circle cx="16" cy="16" r="6"/>
                    <rect x="8" y="2" width="8" height="4" rx="1"/>
                  </svg>
                </button>
              </div>
              <div class="text-xs flex gap-1 group">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 3V8H7"/>
                  <path d="M12 6v6l2 4"/>
                  <path
                      d="M2 12C2 13.9778 2.58649 15.9112 3.6853 17.5557C4.78412 19.2002 6.3459 20.4819 8.17316 21.2388C10.0004 21.9957 12.0111 22.1937 13.9509 21.8078C15.8907 21.422 17.6725 20.4696 19.0711 19.0711C20.4696 17.6725 21.422 15.8907 21.8078 13.9509C22.1937 12.0111 21.9957 10.0004 21.2388 8.17316C20.4819 6.3459 19.2002 4.78412 17.5557 3.6853C15.9112 2.58649 13.9778 2 12 2C9.20439 2.01052 6.52108 3.10136 4.51111 5.04444L2 7.55555"/>
                </svg>
                <p class="text-white">
                  {{ item.state === 'New' ? getTimePassed(item.fireTime) : getTimePassed(item.lastUpdate) }}</p>
              </div>
            </div>
            <div class="flex justify-between items-center text-xs">
              <div class="min-w-20 px-1 py-1 border border-slate-600 rounded-md text-center"
                   :class="[getStepIndex(item.state) === 0 ? 'bg-blue-600' : '']">
                New
              </div>
              <div @click="handleState(item, 1)" class="flex-1 flex items-center gap-1 group"
                   :class="{ 'cursor-pointer': getStepIndex(item.state) === 0 }">
                <div class="flex-1 h-px"
                     :class="getStepIndex(item.state) >= 1 ? 'bg-blue-400' : 'bg-slate-500 group-hover:bg-blue-400'">
                </div>

                <svg v-if="getStepIndex(item.state) === 0" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-check-icon lucide-check stroke-slate-500 group-hover:stroke-blue-400">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
                <svg v-if="getStepIndex(item.state) >= 1" xmlns="http://www.w3.org/2000/svg"
                     width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-check-check-icon lucide-check-check stroke-blue-400">
                  <path d="M18 6 7 17l-5-5"/>
                  <path d="m22 10-7.5 7.5L13 16"/>
                </svg>
                <div class="flex-1 h-px" :class="getStepIndex(item.state) >= 1 ? 'bg-blue-400' : 'bg-slate-500'"></div>
              </div>
              <div class="min-w-20 px-1 py-1 border border-slate-600 rounded-md text-center"
                   :class="[getStepIndex(item.state) === 1 ? 'bg-[#C2410C]' : '']">
                Pending
              </div>
              <div @click="handleState(item, 2)" class="flex-1 flex items-center gap-1 group"
                   :class="{ 'cursor-pointer': getStepIndex(item.state) === 1 }">
                <div class="flex-1 h-px"
                     :class="[getStepIndex(item.state) <= 1 ? 'bg-slate-500' : '',
                     getStepIndex(item.state) === 1 ? 'group-hover:bg-blue-400' : '',
                     getStepIndex(item.state) === 2 ? 'bg-blue-400' : '']">
                </div>
                <svg v-if="getStepIndex(item.state) === 0" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-clock-fading-icon lucide-clock-fading">
                  <path d="M12 2a10 10 0 0 1 7.38 16.75"/>
                  <path d="M12 6v6l4 2"/>
                  <path d="M2.5 8.875a10 10 0 0 0-.5 3"/>
                  <path d="M2.83 16a10 10 0 0 0 2.43 3.4"/>
                  <path d="M4.636 5.235a10 10 0 0 1 .891-.857"/>
                  <path d="M8.644 21.42a10 10 0 0 0 7.631-.38"/>
                </svg>
                <svg v-if="getStepIndex(item.state) === 1" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-check-icon lucide-check stroke-slate-500 group-hover:stroke-blue-400">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
                <svg v-if="getStepIndex(item.state) === 2" xmlns="http://www.w3.org/2000/svg"
                     width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-check-check-icon lucide-check-check stroke-blue-400">
                  <path d="M18 6 7 17l-5-5"/>
                  <path d="m22 10-7.5 7.5L13 16"/>
                </svg>
                <div class="flex-1 h-px" :class="[getStepIndex(item.state) === 2 ? 'bg-blue-400' : 'bg-slate-500']">
                </div>
              </div>
              <div class="min-w-20 px-1 py-1 border border-slate-600 rounded-md text-center"
                   :class="[getStepIndex(item.state) === 2 ? 'bg-green-700' : '']">Completed
              </div>
            </div>
            <div class="flex flex-row gap-2 justify-between text-xs">
              <div class="flex flex-row gap-2 items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 36 36">
                  <path fill="#292f33" d="M16 5h4v3h-4z"/>
                  <path fill="#8899a6"
                        d="M22 1h-8c-1.1 0-2 .9-2 2s.9 2 2 2h8c1.1 0 2-.9 2-2s-.9-2-2-2m6.184 10.507l-1.504-1.318a2.006 2.006 0 0 1-.186-2.822a2.006 2.006 0 0 1 2.822-.186l1.504 1.318c.827.725.911 1.995.186 2.822a2.006 2.006 0 0 1-2.822.186"/>
                  <circle cx="18" cy="21" r="11" fill="#f5f8fa"/>
                  <path fill="#66757f"
                        d="M18 7C10.268 7 4 13.268 4 21s6.268 14 14 14s14-6.268 14-14S25.732 7 18 7m0 25c-6.065 0-11-4.935-11-11s4.935-11 11-11s11 4.935 11 11s-4.935 11-11 11"/>
                  <path fill="#292f33" d="M27 22H17a1 1 0 1 1 0-2h10a1 1 0 1 1 0 2"/>
                  <circle cx="18" cy="12" r="1" fill="#292f33"/>
                  <circle cx="18" cy="30" r="1" fill="#292f33"/>
                  <circle cx="12" cy="15" r="1" fill="#292f33"/>
                  <circle cx="24" cy="27" r="1" fill="#292f33"/>
                  <circle cx="9" cy="21" r="1" fill="#292f33"/>
                  <circle cx="24" cy="15" r="1" fill="#292f33"/>
                  <path fill="#dd2e44"
                        d="M12 28a.999.999 0 0 1-.707-1.707l7-7a.999.999 0 1 1 1.414 1.414l-7 7A1 1 0 0 1 12 28"/>
                </svg>
                <p>{{ item.interval || item.interval === 0 ? intervalOptions[item.interval] : 'One-time only' }}</p>
              </div>
              <!--              <div class="px-2 py-1 border border-slate-600 rounded-md">{{ item.state }}</div>-->
            </div>
            <p>{{ item.orderTitle }}</p>
            <div class="grid grid-cols-2 gap-2">
              <div v-if="item.customer"
                   class="col-span-1 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md p-2 overflow-hidden">
                <div class="cursor-zoom-in shrink-0">
                  <div
                      class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                      <path fill="currentColor"
                            d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                    </svg>
                  </div>
                </div>
                <div class="flex flex-col truncate">
                  <div class="whitespace-nowrap overflow-hidden text-ellipsis">
                    {{ item.customer }}
                  </div>
                  <div class="text-xs text-slate-400 whitespace-nowrap overflow-hidden text-ellipsis">
                    {{ item.customerDiscordUsername ?? "-" }}
                  </div>
                </div>
              </div>
              <div v-if="item.takerID"
                   class="col-span-1 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md p-2 overflow-hidden">
                <div @click.stop="showUserProfileModal(item.takerID)" class="cursor-zoom-in shrink-0">
                  <img v-if="item.takerDiscordAvatar" :src="item.takerDiscordAvatar"
                       alt="avatar" class="size-10 rounded-full border border-slate-700">
                  <div v-else
                       class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                         stroke-linejoin="round" class="lucide lucide-user-icon lucide-user">
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                </div>
                <div class="flex flex-col truncate">
                  <span class="whitespace-nowrap overflow-hidden text-ellipsis">
                    {{ item.takerName }}
                  </span>
                  <span class="text-xs text-slate-400 whitespace-nowrap overflow-hidden text-ellipsis">
                    {{ item.takerDiscordUsername ?? "-" }}{{ "&nbsp- " + item.takerDiscordDisplayName ?? "-" }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="py-4 flex flex-col gap-2 items-center justify-center text-slate-400">
          <svg class="svg-icon"
               style="width: 10em; height: 10em;vertical-align: middle;fill: currentColor;overflow: hidden;"
               viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M491.966645 225.949968c12.479795 0 22.989096-8.538807 22.989096-19.704939s-10.509301-19.704939-22.989096-19.704939-22.989096 8.538807-22.989095 19.704939 10.509301 19.704939 22.989095 19.704939z m0-32.184734c9.195638 0 16.420783 5.911482 16.420783 13.136626s-7.225144 13.136626-16.420783 13.136626-16.420783-5.911482-16.420782-13.136626 7.225144-13.136626 16.420782-13.136626zM597.059654 463.722899c0-40.066709-32.841565-72.908275-72.908275-72.908274s-72.908275 32.841565-72.908274 72.908274 32.841565 72.908275 72.908274 72.908275 72.908275-32.841565 72.908275-72.908275z m-73.565106 63.055805c-34.812059 0-63.055805-28.243746-63.055805-63.055805s28.243746-63.055805 63.055805-63.055805 63.055805 28.243746 63.055805 63.055805c0.656831 34.812059-28.243746 63.055805-63.055805 63.055805zM629.244387 430.881334c-2.627325 0-5.25465 1.970494-5.25465 5.254651s1.970494 5.25465 5.25465 5.25465h69.624118c2.627325 0 5.25465-1.970494 5.254651-5.25465s-1.970494-5.25465-5.254651-5.254651H629.244387zM623.989737 495.907633c0 2.627325 1.970494 5.25465 5.25465 5.254651h108.377165c2.627325 0 5.25465-1.970494 5.254651-5.254651s-1.970494-5.25465-5.254651-5.25465H629.244387c-2.627325 0-5.25465 2.627325-5.25465 5.25465z"
                fill="#FFFFFF"/>
            <path
                d="M527.435536 422.999359c-10.509301 0-18.391276 3.284157-23.645927 9.195638-7.881976 8.538807-7.225144 21.018602-6.568313 21.018601 0 2.627325 2.627325 4.597819 5.25465 4.59782s4.597819-2.627325 4.597819-5.254651c0 0-0.656831-7.881976 4.597819-13.793457 3.284157-3.940988 9.195638-5.911482 16.420783-5.911482 9.195638 0 15.10712 3.940988 16.420782 10.509301 1.313663 6.568313-1.970494 13.136626-7.225144 15.10712-17.077614 6.568313-17.077614 18.391276-17.077614 29.557408v1.313663c0 2.627325 1.970494 5.25465 5.254651 5.25465s5.25465-1.970494 5.25465-5.25465v-1.313663c0-10.509301 0-16.420783 11.166132-20.36177 9.85247-3.940988 15.10712-15.10712 13.136626-26.273252-3.940988-8.538807-11.822963-18.391276-27.586914-18.391276z"
                fill="#FFFFFF"/>
            <path
                d="M524.151379 506.416934m-5.25465 0a5.25465 5.25465 0 1 0 10.509301 0 5.25465 5.25465 0 1 0-10.509301 0Z"
                fill="#FFFFFF"/>
            <path
                d="M144.502886 447.958948c15.10712 0 27.586915-12.479795 27.586915-27.586915s-12.479795-27.586915-27.586915-27.586914c-15.10712 0-27.586915 12.479795-27.586914 27.586914s11.822963 27.586915 27.586914 27.586915z m0-45.978191c9.85247 0 17.734445 7.881976 17.734446 17.734445s-7.881976 17.734445-17.734446 17.734445c-9.85247 0-17.734445-7.881976-17.734445-17.734445s7.881976-17.734445 17.734445-17.734445zM871.615138 500.505452c1.313663 0 2.627325-0.656831 3.284156-1.313662l21.675433-19.704939 20.361771 17.077613c1.970494 1.313663 4.597819 1.313663 6.568313 0l21.675433-17.077613 19.704939 19.048107c1.970494 1.970494 5.25465 1.970494 7.225144 0 1.970494-1.970494 1.970494-5.25465 0-7.225144l-22.989095-22.332264c-1.970494-1.970494-4.597819-1.970494-6.568313 0l-21.675433 17.077614-21.018602-17.077614c-1.970494-1.313663-4.597819-1.313663-6.568313 0l-24.95959 22.332264c-1.970494 1.970494-1.970494 5.25465-0.656831 7.225144 1.313663 1.313663 2.627325 1.970494 3.940988 1.970494zM411.833226 489.996151c2.627325 0 5.25465-1.970494 5.254651-5.25465v-52.546504c0-2.627325-1.970494-5.25465-5.254651-5.254651s-5.25465 1.970494-5.25465 5.254651v52.546504c0 3.284157 2.627325 5.25465 5.25465 5.25465zM406.578576 555.679282c0 12.479795 10.509301 22.989096 22.989096 22.989095h5.911481c2.627325 0 5.25465-1.970494 5.254651-5.25465s-1.970494-5.25465-5.254651-5.254651h-5.911481c-7.225144 0-13.136626-5.911482-13.136626-13.136626v-17.734445c0-2.627325-1.970494-5.25465-5.254651-5.25465s-5.25465 1.970494-5.25465 5.25465v18.391277zM796.079538 405.921745v52.546504c0 2.627325 1.970494 5.25465 5.254651 5.25465s5.25465-1.970494 5.25465-5.25465v-52.546504c0-2.627325-1.970494-5.25465-5.25465-5.254651s-5.25465 2.627325-5.254651 5.254651zM487.368826 348.12059c0-2.627325-1.970494-5.25465-5.25465-5.25465h-52.546504c-12.479795 0-22.989096 10.509301-22.989096 22.989095v13.793458c0 2.627325 1.970494 5.25465 5.25465 5.25465s5.25465-1.970494 5.254651-5.25465v-13.793458c0-7.225144 5.911482-13.136626 13.136626-13.136626h52.546504c2.627325 0.656831 4.597819-1.313663 4.597819-4.597819zM745.503528 353.375241h37.439384c3.940988 0 7.225144 1.313663 9.85247 4.597819 0.656831 1.313663 2.627325 1.313663 3.940987 1.313662 1.313663 0 2.627325-0.656831 3.284157-1.313662 1.970494-1.970494 1.970494-5.25465 0-7.225145-4.597819-4.597819-10.509301-7.881976-17.077614-7.881975h-37.439384c-2.627325 0-5.25465 1.970494-5.254651 5.25465s1.970494 5.25465 5.254651 5.254651zM800.677357 505.760103c-2.627325 0-5.25465 1.970494-5.25465 5.25465v45.32136c0 1.970494-0.656831 3.940988-1.313663 5.25465-1.313663 2.627325 0 5.25465 2.627325 6.568313 0.656831 0 1.313663 0.656831 1.970494 0.656832 1.970494 0 3.940988-1.313663 4.597819-2.627326 1.313663-2.627325 1.970494-5.911482 1.970494-9.195638v-45.32136c0.656831-3.284157-1.970494-5.911482-4.597819-5.911481zM587.864015 353.375241c2.627325 0 5.25465-1.970494 5.254651-5.254651s-1.970494-5.25465-5.254651-5.25465h-52.546504c-2.627325 0-5.25465 1.970494-5.25465 5.25465s1.970494 5.25465 5.25465 5.254651h52.546504zM593.775497 579.325208h52.546504c2.627325 0 5.25465-1.970494 5.254651-5.25465s-1.970494-5.25465-5.254651-5.25465h-52.546504c-2.627325 0-5.25465 1.970494-5.25465 5.25465s1.970494 5.25465 5.25465 5.25465zM488.682489 569.472739c-2.627325 0-5.25465 1.970494-5.254651 5.25465s1.970494 5.25465 5.254651 5.254651h52.546504c2.627325 0 5.25465-1.970494 5.25465-5.254651s-1.970494-5.25465-5.25465-5.25465h-52.546504z"
                fill="#FFFFFF"/>
            <path
                d="M311.994869 719.230276h359.943553c16.420783 0 29.557409-13.136626 29.557409-29.557409V579.325208h49.262347c2.627325 0 5.25465-1.970494 5.254651-5.25465s-1.970494-5.25465-5.254651-5.25465h-49.919179c-0.656831-0.656831-1.313663-0.656831-1.970494-0.656832-1.313663 0-1.970494 0.656831-2.627325 1.313663-1.313663 0.656831-2.627325 2.627325-2.627325 3.940988 0 1.313663 0.656831 2.627325 1.970494 3.940988v111.661321c0 13.136626-10.509301 22.989096-22.989096 22.989095H311.994869c-13.136626 0-22.989096-10.509301-22.989096-22.989095V261.418858c0-13.136626 10.509301-22.989096 22.989096-22.989095h49.919178c0 10.509301 8.538807 19.048108 19.048108 19.048107h221.352149c10.509301 0 19.048108-8.538807 19.048108-19.048107h50.57601c13.136626 0 22.989096 10.509301 22.989096 22.989095v83.417576c-0.656831-0.656831-1.970494-1.313663-2.627326-1.313663h-26.930083v-40.066709c0-16.420783-13.136626-29.557409-29.557409-29.557409H348.777421c-16.420783 0-29.557409 13.136626-29.557408 29.557409v343.522771c0 16.420783 13.136626 29.557409 29.557408 29.557408h287.692111c16.420783 0 29.557409-13.136626 29.557408-29.557408V578.011546c0-1.970494-1.313663-3.284157-3.284156-3.284157s-3.284157 1.313663-3.284157 3.284157v68.967287c0 13.136626-10.509301 22.989096-22.989095 22.989095H348.777421c-13.136626 0-22.989096-10.509301-22.989095-22.989095V303.456062c0-13.136626 10.509301-22.989096 22.989095-22.989096h287.692111c13.136626 0 22.989096 10.509301 22.989095 22.989096v40.066709h-19.048107c-2.627325 0-5.25465 1.970494-5.254651 5.25465s1.970494 5.25465 5.254651 5.254651h52.546504c2.627325 0 4.597819-1.970494 4.597819-4.597819h1.313662c1.970494 0 3.284157-1.313663 3.284157-3.284157V261.418858c0-16.420783-13.136626-29.557409-29.557409-29.557408h-50.57601v-5.911482c0-10.509301-8.538807-19.048108-19.048108-19.048108h-62.398973c0-21.675433-21.675433-40.066709-47.948685-40.066709s-47.948685 17.734445-47.948685 40.066709H380.962155c-10.509301 0-19.048108 8.538807-19.048108 19.048108v5.911482h-49.919178c-16.420783 0-29.557409 13.136626-29.557409 29.557408V689.672867c0 16.420783 13.136626 29.557409 29.557409 29.557409z m56.487491-493.937139c0-6.568313 5.911482-12.479795 12.479795-12.479795h70.28095l-0.656832-3.940988v-3.284156c0-18.391276 18.391276-33.498396 41.380372-33.498397s41.380372 15.10712 41.380372 33.498397v3.284156l-0.656831 3.940988h69.624118c6.568313 0 12.479795 5.911482 12.479795 12.479795v12.479794c0 6.568313-5.911482 12.479795-12.479795 12.479795H380.962155c-6.568313 0-12.479795-5.911482-12.479795-12.479795v-12.479794zM222.00898 800.020526h580.638871c2.627325 0 5.25465-1.970494 5.254651-5.25465s-1.970494-5.25465-5.254651-5.254651H222.00898c-2.627325 0-5.25465 1.970494-5.25465 5.254651s2.627325 5.25465 5.25465 5.25465zM995.756254 836.803079h-289.005773c-2.627325 0-5.25465 1.970494-5.25465 5.25465s1.970494 5.25465 5.25465 5.254651h289.005773c2.627325 0 5.25465-1.970494 5.25465-5.254651s-2.627325-5.25465-5.25465-5.25465zM679.163566 836.803079h-40.72354c-2.627325 0-5.25465 1.970494-5.254651 5.25465s1.970494 5.25465 5.254651 5.254651h40.72354c2.627325 0 5.25465-1.970494 5.254651-5.254651s-1.970494-5.25465-5.254651-5.25465zM611.509942 836.803079H21.018602c-2.627325 0-5.25465 1.970494-5.254651 5.25465s1.970494 5.25465 5.254651 5.254651h590.49134c2.627325 0 5.25465-1.970494 5.254651-5.254651s-2.627325-5.25465-5.254651-5.25465zM857.821681 708.720975c17.077614 0 30.871071-13.793457 30.871071-30.871071s-13.793457-30.871071-30.871071-30.871071-30.871071 13.793457-30.871072 30.871071c0.656831 17.077614 14.450289 30.871071 30.871072 30.871071z m0-51.232842c11.822963 0 21.018602 9.195638 21.018601 21.018602 0 11.822963-9.195638 21.018602-21.018601 21.018602-11.822963 0-21.018602-9.195638-21.018602-21.018602 0.656831-11.822963 9.85247-21.018602 21.018602-21.018602zM47.948685 634.499038c1.313663 0 2.627325-0.656831 3.284157-1.313663l21.675433-19.704939 20.36177 17.077614c1.970494 1.313663 4.597819 1.313663 6.568313 0l21.675433-17.077614 19.704939 19.048108c1.970494 1.970494 5.25465 1.970494 7.225144 0 1.970494-1.970494 1.970494-5.25465 0-7.225144L124.797947 603.627967c-1.970494-1.970494-4.597819-1.970494-6.568313 0l-21.675433 17.077614-21.018601-17.077614c-1.970494-1.313663-4.597819-1.313663-6.568313 0l-24.95959 22.332264c-1.970494 1.970494-1.970494 5.25465-0.656831 7.225144 1.970494 0.656831 3.284157 1.313663 4.597819 1.313663z"
                fill="#FFFFFF"/>
          </svg>
          <p>No updatedog yet{{ hotOrdersStore.activeFilterCount ? ' with this filter.' : '.' }}</p>
        </div>
      </div>
    </div>
    <LogModal v-if="isShowItemLogModal" :item="selectedItem"/>
    <FiltersModal v-if="isShowFiltersModal"/>
    <UserProfile v-if="isShowUserProfileModal" :userID="selectedTakerID"/>
  </div>
</template>
